import { useEffect, useState, useCallback } from "react";
import api from "../api";
import { useI18n } from "../i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "../components/FormField";
import { eur } from "../helpers";
import AuditDetailDialog from "../components/AuditDetailDialog";
import { Download, Filter, RotateCcw, Lock, RefreshCw, ShieldAlert, ChevronLeft, ChevronRight, Search, Eye } from "lucide-react";

const ACTION_STYLE = {
  login: "bg-emerald-100 text-emerald-700", login_failed: "bg-orange-100 text-orange-700",
  login_suspicious: "bg-red-100 text-red-700", logout: "bg-slate-100 text-slate-600",
  password_change: "bg-violet-100 text-violet-700", password_reset: "bg-violet-100 text-violet-700",
  password_reset_request: "bg-violet-50 text-violet-600",
  user_create: "bg-teal-100 text-teal-700", user_update: "bg-blue-100 text-blue-700",
  user_activate: "bg-emerald-100 text-emerald-700", user_deactivate: "bg-amber-100 text-amber-800",
  user_delete: "bg-red-100 text-red-700", user_role_change: "bg-fuchsia-100 text-fuchsia-700",
  user_permissions_change: "bg-fuchsia-100 text-fuchsia-700",
  apartment_create: "bg-teal-100 text-teal-700", apartment_update: "bg-blue-100 text-blue-700",
  apartment_delete: "bg-amber-100 text-amber-800", booking_create: "bg-teal-100 text-teal-700",
  booking_update: "bg-blue-100 text-blue-700", booking_delete: "bg-red-100 text-red-700",
  block_create: "bg-indigo-100 text-indigo-700", block_update: "bg-blue-100 text-blue-700",
  block_delete: "bg-slate-200 text-slate-700", booking_block_override: "bg-amber-100 text-amber-800",
  refund_create: "bg-rose-100 text-rose-700", refund_void: "bg-slate-100 text-slate-600",
  cost_update: "bg-cyan-100 text-cyan-700", cost_delete: "bg-red-100 text-red-700",
  company_cost_update: "bg-cyan-100 text-cyan-700", settings_update: "bg-purple-100 text-purple-700",
  export_csv: "bg-slate-100 text-slate-600", export_pdf: "bg-slate-100 text-slate-600",
  owner_report_sent: "bg-sky-100 text-sky-700",
};
const fmtDT = (s) => { try { return new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return s; } };
const ymd = (d) => d.toISOString().slice(0, 10);
const emptyFilters = { date_from: "", date_to: "", user: "all", apartment_id: "all", action: "all" };

export default function AuditLog() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ users: [], actions: [] });
  const [apts, setApts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState(emptyFilters);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [quick, setQuick] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [detailId, setDetailId] = useState(null);

  const params = useCallback(() => {
    const p = { page, page_size: pageSize };
    Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") p[k] = v; });
    if (search.trim()) p.search = search.trim();
    return p;
  }, [f, search, page, pageSize]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/audit-logs", { params: params() })
      .then((r) => { setRows(r.data.items); setTotal(r.data.total); setPages(r.data.pages); })
      .catch((e) => toast.error(e.response?.data?.detail || "Errore nel caricamento"))
      .finally(() => setLoading(false));
  }, [params]);

  useEffect(() => {
    api.get("/audit-logs/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/apartments/lookup").then((r) => setApts(r.data)).catch(() => {});
    const mid = new URLSearchParams(window.location.search).get("movimento");
    if (mid) setDetailId(mid);
  }, []);
  useEffect(() => { load(); }, [load]);
  // reset to page 1 whenever filters/search change
  const setFilter = (patch) => { setQuick(""); setPage(1); setF((s) => ({ ...s, ...patch })); };

  const applyQuick = (kind) => {
    const now = new Date();
    let from = "";
    if (kind === "7") from = ymd(new Date(now.getTime() - 6 * 864e5));
    else if (kind === "30") from = ymd(new Date(now.getTime() - 29 * 864e5));
    else if (kind === "month") from = ymd(new Date(now.getFullYear(), now.getMonth(), 1));
    setPage(1); setQuick(kind);
    setF((s) => ({ ...s, date_from: from, date_to: ymd(now) }));
  };

  const runSearch = () => { setPage(1); setSearch(searchInput); };
  const reset = () => { setF(emptyFilters); setSearch(""); setSearchInput(""); setQuick(""); setPage(1); };

  const downloadCsv = async () => {
    try {
      const p = { ...params() }; delete p.page; delete p.page_size;
      const r = await api.get("/audit-logs/csv", { params: p, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "storico_movimenti.csv"; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Errore nell'esportazione"); }
  };

  const QuickBtn = ({ k, label }) => (
    <button data-testid={`audit-quick-${k}`} onClick={() => applyQuick(k)}
      className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${quick === k ? "border-primary bg-primary text-primary-foreground" : "border-slate-300 text-slate-600 hover:bg-slate-100"}`}>
      {label}
    </button>
  );

  return (
    <div className="space-y-6 fade-in" data-testid="audit-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <ShieldAlert size={16} className="text-slate-400" />
          Registro immutabile delle azioni. Ordinato dal più recente. Non modificabile né eliminabile.
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" data-testid="audit-refresh" onClick={load}><RefreshCw size={15} /> Aggiorna</Button>
          <Button size="sm" className="gap-1.5 bg-primary hover:bg-primary/90" data-testid="audit-export-csv" onClick={downloadCsv}><Download size={15} /> Esporta CSV</Button>
        </div>
      </div>

      {/* Quick filters */}
      <div className="flex flex-wrap items-center gap-2" data-testid="audit-quick-filters">
        <span className="text-xs font-semibold uppercase text-slate-400">Periodo rapido:</span>
        <QuickBtn k="7" label="Ultimi 7 giorni" />
        <QuickBtn k="30" label="Ultimi 30 giorni" />
        <QuickBtn k="month" label="Mese corrente" />
      </div>

      {/* Filters */}
      <Card className="border-slate-200 p-4" data-testid="audit-filters">
        <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase text-slate-500"><Filter size={14} /> Filtri</div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          <Field label="Dal"><Input type="date" data-testid="audit-from" value={f.date_from} onChange={(e) => setFilter({ date_from: e.target.value })} /></Field>
          <Field label="Al"><Input type="date" data-testid="audit-to" value={f.date_to} onChange={(e) => setFilter({ date_to: e.target.value })} /></Field>
          <Field label="Tipo azione">
            <Select value={f.action} onValueChange={(v) => setFilter({ action: v })}>
              <SelectTrigger data-testid="audit-action"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte</SelectItem>
                {meta.actions.map((a) => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Utente">
            <Select value={f.user} onValueChange={(v) => setFilter({ user: v })}>
              <SelectTrigger data-testid="audit-user"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {meta.users.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Appartamento">
            <Select value={f.apartment_id} onValueChange={(v) => setFilter({ apartment_id: v })}>
              <SelectTrigger data-testid="audit-apartment"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {apts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Ricerca (utente/oggetto/prenotazione)">
            <div className="flex gap-1">
              <Input data-testid="audit-search" placeholder="Cerca…" value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }} />
              <Button size="icon" variant="outline" className="shrink-0" data-testid="audit-search-btn" onClick={runSearch}><Search size={15} /></Button>
            </div>
          </Field>
        </div>
        <div className="mt-3 flex justify-end">
          <Button variant="ghost" size="sm" className="gap-1.5 text-slate-500" data-testid="audit-reset" onClick={reset}><RotateCcw size={14} /> Azzera filtri</Button>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-3">Data e ora</th>
                <th className="px-3 py-3">Azione</th>
                <th className="px-3 py-3">Utente</th>
                <th className="px-3 py-3">IP / Dispositivo</th>
                <th className="px-3 py-3">Oggetto</th>
                <th className="px-3 py-3">Valore prec.</th>
                <th className="px-3 py-3">Valore nuovo</th>
                <th className="px-3 py-3">Motivazione</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`audit-row-${r.id}`} className="border-b border-slate-100 align-top hover:bg-slate-50">
                  <td className="whitespace-nowrap px-3 py-3 text-slate-600">{fmtDT(r.at)}</td>
                  <td className="px-3 py-3">
                    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${ACTION_STYLE[r.action] || "bg-slate-100 text-slate-600"}`}>
                      <Lock size={11} /> {r.action_label}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-slate-700">{r.user_email || "—"}<div className="text-[11px] text-slate-400">{r.user_role}</div></td>
                  <td className="px-3 py-3 text-slate-600"><span className="font-mono text-xs">{r.ip || "—"}</span><div className="text-[11px] text-slate-400">{r.device || ""}</div></td>
                  <td className="max-w-[200px] px-3 py-3 text-slate-700">
                    {r.object_label}
                    {r.amount != null && <span className="ml-1 font-semibold text-rose-600">({eur(r.amount)})</span>}
                    {r.booking_ref && r.object_label !== r.booking_ref && <div className="text-[11px] text-slate-400">Pren.: {r.booking_ref}</div>}
                  </td>
                  <td className="max-w-[200px] px-3 py-3 text-[11px] text-slate-500">{r.old_value || "—"}</td>
                  <td className="max-w-[200px] px-3 py-3 text-[11px] text-slate-700">{r.new_value || "—"}</td>
                  <td className="max-w-[180px] px-3 py-3 text-slate-600">{r.reason || "—"}{r.internal_notes && <div className="text-[11px] text-slate-400">{r.internal_notes}</div>}</td>
                  <td className="px-3 py-3">
                    <Button data-testid={`audit-open-${r.id}`} size="sm" variant="outline" className="gap-1" onClick={() => setDetailId(r.id)}><Eye size={14} /> Apri</Button>
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">Nessun movimento registrato per i filtri selezionati.</td></tr>}
              {loading && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">Caricamento…</td></tr>}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-sm text-slate-500">
          <div data-testid="audit-total">{total} movimenti · pagina {page} di {pages}</div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="gap-1" data-testid="audit-prev" disabled={page <= 1} onClick={() => setPage((p) => Math.max(p - 1, 1))}><ChevronLeft size={15} /> Precedente</Button>
            <Button size="sm" variant="outline" className="gap-1" data-testid="audit-next" disabled={page >= pages} onClick={() => setPage((p) => Math.min(p + 1, pages))}>Successiva <ChevronRight size={15} /></Button>
          </div>
        </div>
      </Card>

      <AuditDetailDialog open={!!detailId} onOpenChange={(v) => !v && setDetailId(null)} logId={detailId} />
    </div>
  );
}
